import { useEffect, useMemo, useState } from "react";
import { Loader2, MapPin } from "lucide-react";
import api from "@/lib/api";

const TILES = {
  AK: [0, 0], ME: [10, 0],
  VT: [9, 1], NH: [10, 1],
  WA: [0, 2], ID: [1, 2], MT: [2, 2], ND: [3, 2], MN: [4, 2], IL: [5, 2], WI: [6, 2], MI: [7, 2], NY: [8, 2], RI: [9, 2], MA: [10, 2],
  OR: [0, 3], NV: [1, 3], WY: [2, 3], SD: [3, 3], IA: [4, 3], IN: [5, 3], OH: [6, 3], PA: [7, 3], NJ: [8, 3], CT: [9, 3],
  CA: [0, 4], UT: [1, 4], CO: [2, 4], NE: [3, 4], MO: [4, 4], KY: [5, 4], WV: [6, 4], VA: [7, 4], MD: [8, 4], DE: [9, 4],
  AZ: [1, 5], NM: [2, 5], KS: [3, 5], AR: [4, 5], TN: [5, 5], NC: [6, 5], SC: [7, 5], DC: [8, 5],
  OK: [3, 6], LA: [4, 6], MS: [5, 6], AL: [6, 6], GA: [7, 6],
  HI: [0, 7], TX: [3, 7], FL: [7, 7],
};

const NET_COLORS = { attorney: "text-cobalt", rn: "text-rose-600", telehealth: "text-emerald-600", drone: "text-amber-600" };
const NET_LABELS = { attorney: "Attorneys", rn: "RNs", telehealth: "Telehealth", drone: "Drone Pilots" };

export default function NetworkMap() {
  const [data, setData] = useState(null);
  const [profession, setProfession] = useState("");
  const [availability, setAvailability] = useState("");
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    setData(null);
    const params = {};
    if (profession) params.profession = profession;
    if (availability) params.availability = availability;
    api.get("/network-command/map", { params }).then(({ data }) => setData(data)).catch(() => {});
  }, [profession, availability]);

  const maxCount = useMemo(() => Math.max(1, ...Object.values(data?.states || {}).map((s) => s.total)), [data]);
  const stateMembers = picked ? (data?.members || []).filter((m) => m.states.includes(picked)) : [];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><MapPin className="w-5 h-5 text-cobalt" /> Global Network Map</h3>
            <p className="text-sm text-slate-500 mt-1">Live national coverage across all professional networks. Click any state to drill down.</p>
          </div>
          <div className="flex gap-2">
            <select value={profession} onChange={(e) => { setProfession(e.target.value); setPicked(null); }} data-testid="nc-map-profession-filter"
              className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white">
              <option value="">All professions</option>
              <option value="attorney">Attorneys</option><option value="rn">Registered Nurses</option>
              <option value="telehealth">Telehealth Providers</option><option value="drone">Drone Pilots</option>
            </select>
            <select value={availability} onChange={(e) => { setAvailability(e.target.value); setPicked(null); }} data-testid="nc-map-availability-filter"
              className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white">
              <option value="">Any availability</option>
              <option value="available">Available</option><option value="busy">Busy</option><option value="offline">Offline</option>
            </select>
          </div>
        </div>

        {!data ? <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div> : (
          <>
            <div className="mt-4 text-sm text-slate-500" data-testid="nc-map-total">
              <span className="font-heading font-extrabold text-navy text-lg">{data.total}</span> active &amp; pending professionals across{" "}
              <span className="font-semibold text-navy">{Object.keys(data.states).length}</span> states
            </div>
            <div className="mt-4 overflow-x-auto">
              <div className="relative min-w-[640px]" style={{ display: "grid", gridTemplateColumns: "repeat(11, 1fr)", gap: 4 }}>
                {Array.from({ length: 88 }).map((_, i) => {
                  const col = i % 11, row = Math.floor(i / 11);
                  const abbr = Object.keys(TILES).find((k) => TILES[k][0] === col && TILES[k][1] === row);
                  if (!abbr) return <div key={i} />;
                  const sd = data.states[abbr];
                  const intensity = sd ? Math.min(1, 0.25 + (sd.total / maxCount) * 0.75) : 0;
                  return (
                    <button key={i} onClick={() => setPicked(picked === abbr ? null : abbr)} data-testid={`nc-map-state-${abbr}`}
                      className={`aspect-square rounded-lg flex flex-col items-center justify-center text-[10px] font-bold transition-all hover:scale-105 ${picked === abbr ? "ring-2 ring-navy" : ""}`}
                      style={{ backgroundColor: sd ? `rgba(37, 99, 235, ${intensity})` : "#f1f5f9", color: sd && intensity > 0.5 ? "white" : "#334155" }}>
                      {abbr}
                      {sd && <span className="text-[9px] font-semibold">{sd.total}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-slate-400">
              <span>Coverage density:</span>
              <div className="h-3 w-24 rounded-full" style={{ background: "linear-gradient(to right, #f1f5f9, #2563eb)" }} />
              <span>low → high</span>
            </div>
          </>
        )}
      </div>

      {picked && data && (
        <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="nc-map-state-panel">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-navy">{picked} — {stateMembers.length} professional{stateMembers.length === 1 ? "" : "s"}</h3>
            <div className="flex gap-3 text-xs">
              {Object.entries(NET_LABELS).map(([k, l]) => (
                <span key={k} className={`font-semibold ${NET_COLORS[k]}`}>{l}: {data.states[picked]?.[k] || 0}</span>
              ))}
            </div>
          </div>
          <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {stateMembers.map((m) => (
              <div key={`${m.network}-${m.id}`} className="rounded-2xl border border-slate-100 p-3" data-testid={`nc-map-member-${m.id}`}>
                <div className="text-sm font-medium text-navy">{m.name || "Unnamed"}</div>
                <div className={`text-xs font-semibold ${NET_COLORS[m.network]}`}>{NET_LABELS[m.network]}</div>
                <div className="text-xs text-slate-400 mt-0.5">{m.organization || "Independent"} · {m.availability || "n/a"} · {m.status}</div>
              </div>
            ))}
            {stateMembers.length === 0 && <div className="text-sm text-slate-400 py-4">No professionals in this state for the current filters.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
